import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { Segmented, Flex, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { getColor, rgbaColor, numberFormatter, formatCurrency } from '../../../helpers/utils';
import AppContext from '../../../context/Context';
import SnapshotService from '../services';
import CardEmptyResult from 'components/common/pageComponents/CardEmptyResult';
import { PiReceiptDuotone } from 'react-icons/pi';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent
]);

const BillSnapshotChart = ({ groupBy = 'day', startDate, endDate, status, statusPayment, supplierId }) => {
  const context = useContext(AppContext);
  const isDark = false;

  const [currency_symbol, setCurrencySymbol] = useState('$');
  const [data, setData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentGroupBy, setCurrentGroupBy] = useState(groupBy);

  const getData = useCallback(() => {
    setIsLoading(true);
    const params = {
      group_by: currentGroupBy
    };
    
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (status) params.status = status;
    if (statusPayment) params.status_payment = statusPayment;
    if (supplierId) params.supplier_id = supplierId;

    SnapshotService.getBillSnapshots(params).then((response) => {
      try {
        if (response.data?.success && response.data?.data) {
          const { data: chartData, currency, labels: chartLabels } = response.data.data;
          setData(chartData);
          setLabels(chartLabels || chartData.map(item => item.date_issued || item.year_month || item.year));
          setCurrencySymbol(currency?.currency_symbol || '$');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching bill snapshot data:', error);
        setIsLoading(false);
      }
    }).catch(error => {
      console.error('Error fetching bill snapshot data:', error);
      setIsLoading(false);
    });
  }, [currentGroupBy, startDate, endDate, status, statusPayment, supplierId]);

  useEffect(() => {
    getData();
  }, [getData]);

  const changeGroupBy = useCallback((value) => {
    setCurrentGroupBy(value);
  }, []);

  const tooltipFormatter = useCallback((params) => {
    if (!Array.isArray(params) || params.length === 0) {
      return '';
    }

    const firstParam = params[0];
    const periodLabel = firstParam.name || '';
    
    let total = 0;
    let paid = 0;
    
    params.forEach(param => {
      const value = typeof param.value === 'object' ? param.value[1] : param.value;
      const numValue = parseFloat(value) || 0;
      
      if (param.seriesName === 'Total') {
        total = numValue;
      } else if (param.seriesName === 'Paid') {
        paid = numValue;
      }
    });

    return `
      <div style="padding: 8px;">
        <div style="font-weight: 600; margin-bottom: 4px;">${periodLabel}</div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('danger')}; margin-right: 6px;"></span>
          Total: ${formatCurrency(total, currency_symbol)}
        </div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('success')}; margin-right: 6px;"></span>
          Paid: ${formatCurrency(paid, currency_symbol)}
        </div>
      </div>
    `;
  }, [currency_symbol]);

  const totals = useMemo(() => data.map(item => parseFloat(item.total) || 0), [data]);
  const paid = useMemo(() => data.map(item => parseFloat(item.paid) || 0), [data]);

  const getOptions = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      formatter: tooltipFormatter,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'transparent',
      textStyle: { color: '#fff' }
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisPointer: { type: 'shadow' },
      axisLabel: {
        color: getColor('gray-400'),
        rotate: labels.length > 12 ? 45 : 0
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    yAxis: {
      type: 'value',
      axisPointer: { show: false },
      splitLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'dashed'
        }
      },
      boundaryGap: false,
      axisLabel: {
        show: true,
        color: getColor('gray-400'),
        formatter: (value) => {
          return `${currency_symbol} ${numberFormatter(value, 0)}`
        },
        margin: 15
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    series: [
      {
        name: 'Total',
        type: 'line',
        data: totals,
        lineStyle: { color: getColor('danger') },
        itemStyle: {
          borderColor: getColor('danger'),
          borderWidth: 1
        },
        symbol: 'circle',
        symbolSize: 4,
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: rgbaColor(getColor('danger'), 0.2) },
              { offset: 1, color: rgbaColor(getColor('danger'), 0) }
            ]
          }
        }
      },
      {
        name: 'Paid',
        type: 'line',
        data: paid,
        lineStyle: { color: getColor('success') },
        itemStyle: {
          borderColor: getColor('success'),
          borderWidth: 1
        },
        symbol: 'circle',
        symbolSize: 4,
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: rgbaColor(getColor('success'), 0.2) },
              { offset: 1, color: rgbaColor(getColor('success'), 0) }
            ]
          }
        }
      }
    ],
    legend: {
      data: ['Total', 'Paid'],
      bottom: 0,
      textStyle: {
        color: getColor('gray-400')
      }
    },
    grid: { right: 10, left: 0, bottom: 30, top: 10, containLabel: true }
  }), [labels, currency_symbol, tooltipFormatter, totals, paid]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Spin indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  const hasData =
    data.length > 0 &&
    (totals.some(value => value > 0) || paid.some(value => value > 0));

  if (!hasData) {
    return (
      <CardEmptyResult
        title="No bills data"
        subtitle="There is no bills activity available for the selected period."
        icon={<PiReceiptDuotone className="fs-2 text-secondary" />}
      />
    );
  }

  return (
    <>
      <Flex justify="space-between" align="center">
        <h6>Bills Overview</h6>
        <Segmented
          options={[
            { label: 'Day', value: 'day' },
            { label: 'Week', value: 'week' },
            { label: 'Month', value: 'month' },
            { label: 'Year', value: 'year' },
          ]}
          value={currentGroupBy}
          onChange={changeGroupBy}
        />
      </Flex>
      <ReactEChartsCore
        echarts={echarts}
        option={getOptions}
        style={{ height: '20.4375rem' }}
      />
    </>
  );
};

export default BillSnapshotChart;

